/**
 * CronPanel — управление cron (per-user crontab + system cron files).
 *
 * Backend: /api/v1/devops/cron/{users,user/{u},user(POST),system}.
 */

import { lazy, Suspense, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  RefreshCw,
  Save,
  Loader2,
  Users,
  FileText,
  ShieldAlert,
} from "lucide-react";
import {
  fetchCronUsers,
  fetchUserCrontab,
  writeUserCrontab,
  fetchSystemCrons,
  fetchHostFile,
} from "../../lib/api";
import { cn } from "../../lib/cn";

const CodeMirrorEditor = lazy(() => import("../files/CodeMirrorEditor"));

type Section = "users" | "system";

export function CronPanel() {
  const [section, setSection] = useState<Section>("users");

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        <SectionBtn
          active={section === "users"}
          onClick={() => setSection("users")}
          icon={<Users className="size-3.5" />}
          label="User crontabs"
        />
        <SectionBtn
          active={section === "system"}
          onClick={() => setSection("system")}
          icon={<FileText className="size-3.5" />}
          label="/etc/cron.*"
        />
      </div>
      {section === "users" && <UsersSection />}
      {section === "system" && <SystemSection />}
    </div>
  );
}

function SectionBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md text-small inline-flex items-center gap-1.5 border",
        active
          ? "bg-accent-500/10 text-accent-500 border-accent-500/40"
          : "border-border/60 text-fg-muted hover:bg-bg-subtle",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function UsersSection() {
  const [users, setUsers] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newUser, setNewUser] = useState("");

  async function loadUsers() {
    try {
      const r = await fetchCronUsers();
      setUsers(r.users);
      if (!selected && r.users.length > 0) {
        await selectUser(r.users[0]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function selectUser(u: string) {
    setSelected(u);
    setError(null);
    try {
      const r = await fetchUserCrontab(u);
      const c = r.success ? r.content : "";
      setContent(c);
      setOriginal(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function save(confirmed: boolean) {
    if (!selected) return;
    setBusy(true);
    try {
      await writeUserCrontab(selected, content, { confirm: confirmed });
      setOriginal(content);
      setConfirmOpen(false);
      await loadUsers();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ")) {
        setConfirmOpen(true);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  const dirty = content !== original;

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-3">
      <div className="space-y-2">
        <div className="flex gap-1">
          <input
            type="text"
            value={newUser}
            onChange={(e) => setNewUser(e.target.value)}
            placeholder="root, www-data..."
            className="flex-1 px-2 py-1 rounded border border-border/60 bg-bg-card text-small font-mono"
          />
          <button
            type="button"
            onClick={() => newUser && selectUser(newUser)}
            className="px-2 py-1 rounded border border-border/60 hover:bg-bg-subtle text-small"
          >
            Открыть
          </button>
        </div>
        <div className="rounded-modal border border-border/60 bg-bg-card/40 max-h-[60vh] overflow-auto">
          {users.length === 0 && (
            <div className="p-3 text-fg-muted text-small">
              <Loader2 className="size-3.5 animate-spin inline mr-1" />
              нет users
            </div>
          )}
          {users.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => selectUser(u)}
              className={cn(
                "block w-full text-left px-3 py-1.5 hover:bg-bg-subtle/50 text-small font-mono border-b border-border/20",
                selected === u && "bg-accent-500/10 text-accent-500",
              )}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {!selected ? (
          <div className="rounded-modal border border-border/60 bg-bg-card/40 p-6 text-fg-muted text-small text-center">
            Выберите пользователя слева
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-accent-500" />
              <div className="font-mono text-small flex-1">crontab -u {selected}</div>
              {dirty && <span className="text-[10px] uppercase font-mono text-warning">несохранено</span>}
              <button
                type="button"
                onClick={() => save(false)}
                disabled={busy || !dirty}
                className="px-2 py-1 rounded text-[11px] border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 disabled:opacity-40 inline-flex items-center gap-1"
              >
                {busy ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" />}
                Сохранить
              </button>
            </div>
            {confirmOpen && (
              <div className="rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2">
                <ShieldAlert className="size-4 mt-0.5 text-warning shrink-0" />
                <div className="flex-1">
                  <div className="text-small font-medium">Запись заменит весь crontab пользователя</div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => save(true)}
                      className="px-3 py-1.5 rounded-md text-small font-medium text-white"
                      style={{ backgroundImage: "var(--grad-cta)" }}
                    >
                      Подтверждаю
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmOpen(false)}
                      className="px-3 py-1.5 rounded-md text-small border border-border/60"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              </div>
            )}
            {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
            <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden h-[60vh]">
              <Suspense
                fallback={
                  <div className="p-4 text-fg-muted text-small">
                    <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка…
                  </div>
                }
              >
                <CodeMirrorEditor content={content} langKey="plain" onChange={(v) => setContent(v)} />
              </Suspense>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SystemSection() {
  const [items, setItems] = useState<Array<{ path: string; kind: string }>>([]);
  const [openFile, setOpenFile] = useState<{ path: string; content: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await fetchSystemCrons();
      setItems(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function openItem(p: string) {
    try {
      const f = await fetchHostFile(p);
      setOpenFile({ path: f.path, content: f.content });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-3">
      <div className="rounded-modal border border-border/60 bg-bg-card/40 max-h-[70vh] overflow-auto">
        {loading ? (
          <div className="p-3 text-fg-muted text-small">
            <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка…
          </div>
        ) : (
          items.map((it) => (
            <button
              key={it.path}
              type="button"
              onClick={() => openItem(it.path)}
              className="block w-full text-left px-3 py-1.5 hover:bg-bg-subtle/50 text-small font-mono border-b border-border/20"
            >
              <div className="text-fg truncate">{it.path}</div>
              <div className="text-[10px] text-fg-subtle">{it.kind}</div>
            </button>
          ))
        )}
      </div>
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden min-h-[60vh]">
        {!openFile ? (
          <div className="p-6 text-fg-muted text-small text-center">Выберите файл слева</div>
        ) : (
          <>
            <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 font-mono text-small">
              {openFile.path}
            </div>
            <pre className="p-3 text-[12px] font-mono whitespace-pre-wrap text-fg max-h-[60vh] overflow-auto">
              {openFile.content || "(пусто)"}
            </pre>
          </>
        )}
        {error && <div className="m-2 rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      </div>
    </div>
  );
}
